import { useEffect } from "react";

/** Global flourish layer for ASTRONATLAS:
 *   1. Magnetic space-themed cursor (dot + ring with easing + hover state)
 *   2. Parallax stars driven by scrollY / mouse
 *   3. IntersectionObserver-based "flow-in" reveals on every article-style card
 *   4. Cursor-follow neon glow across the top nav
 *  Pure vanilla — no GSAP / Framer deps. Auto-disables on touch + reduced-motion. */
export default function InteractiveEffects() {
  useEffect(() => {
    const coarse = window.matchMedia("(pointer: coarse)").matches;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // --- cleanup registry ---
    const cleanups = [];

    /* ---------- 1. MAGNETIC CURSOR ---------- */
    let cursor, ring;
    if (!coarse && !reduced) {
      cursor = document.createElement("div");
      cursor.className = "sat-cursor";
      cursor.setAttribute("data-testid", "sat-cursor");
      cursor.innerHTML = `
        <span class="sat-cursor__dot"></span>
        <span class="sat-cursor__ring"></span>
      `;
      document.body.appendChild(cursor);
      document.body.classList.add("sat-cursor-on");

      const state = { mx: -100, my: -100, rx: -100, ry: -100, active: false };
      const MAGNET_R = 80;
      const HOTS = "a[href],button,[role=button],[data-testid],input,textarea,select,summary,label[for]";

      const findMagnet = (x, y) => {
        // Prefer whatever is directly under the cursor when it's an interactive.
        const el = document.elementFromPoint(x, y);
        const near = el?.closest?.(HOTS);
        if (!near) return null;
        const r = near.getBoundingClientRect();
        if (r.width > 380 || r.height > 120) return null; // ignore large containers
        return { el: near, r };
      };

      const onMove = (e) => {
        state.mx = e.clientX;
        state.my = e.clientY;
        const m = findMagnet(e.clientX, e.clientY);
        state.active = !!m;
        if (m) {
          const cx = m.r.left + m.r.width / 2;
          const cy = m.r.top + m.r.height / 2;
          const dx = state.mx - cx;
          const dy = state.my - cy;
          const dist = Math.hypot(dx, dy);
          if (dist < MAGNET_R) {
            // Pull the ring toward the element's center (up to 40% pull).
            state.mx = cx + dx * 0.6;
            state.my = cy + dy * 0.6;
          }
        }
      };
      const onDown = () => cursor.classList.add("is-down");
      const onUp = () => cursor.classList.remove("is-down");
      const onLeave = () => { cursor.style.opacity = "0"; };
      const onEnter = () => { cursor.style.opacity = "1"; };

      window.addEventListener("mousemove", onMove, { passive: true });
      window.addEventListener("mousedown", onDown);
      window.addEventListener("mouseup", onUp);
      document.documentElement.addEventListener("mouseleave", onLeave);
      document.documentElement.addEventListener("mouseenter", onEnter);

      let raf;
      const tick = () => {
        // Ring lags behind mouse (spring); dot snaps to mouse.
        state.rx += (state.mx - state.rx) * 0.18;
        state.ry += (state.my - state.ry) * 0.18;
        cursor.style.setProperty("--x", `${state.mx}px`);
        cursor.style.setProperty("--y", `${state.my}px`);
        cursor.style.setProperty("--rx", `${state.rx}px`);
        cursor.style.setProperty("--ry", `${state.ry}px`);
        cursor.classList.toggle("is-active", state.active);
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);

      cleanups.push(() => {
        cancelAnimationFrame(raf);
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mousedown", onDown);
        window.removeEventListener("mouseup", onUp);
        document.documentElement.removeEventListener("mouseleave", onLeave);
        document.documentElement.removeEventListener("mouseenter", onEnter);
        cursor.remove();
        document.body.classList.remove("sat-cursor-on");
      });
    }

    /* ---------- 2. PARALLAX STARS (scroll + mouse) ---------- */
    let starRaf, starTargetY = 0, starY = 0, starMX = 0, starMY = 0;
    const stars = document.querySelector(".sat-stars");
    if (stars && !reduced) {
      const onScroll = () => { starTargetY = window.scrollY; };
      const onMouseParallax = (e) => {
        // -0.5..0.5 offset from centre
        starMX = (e.clientX / window.innerWidth - 0.5) * 30;
        starMY = (e.clientY / window.innerHeight - 0.5) * 20;
      };
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("mousemove", onMouseParallax, { passive: true });
      const step = () => {
        starY += (starTargetY - starY) * 0.08;
        // Two layers move at different rates for depth.
        const y1 = -starY * 0.15 + starMY;
        const y2 = -starY * 0.35 + starMY * 1.6;
        const x = starMX;
        stars.style.setProperty("--stars-y1", `${y1}px`);
        stars.style.setProperty("--stars-y2", `${y2}px`);
        stars.style.setProperty("--stars-x", `${x}px`);
        starRaf = requestAnimationFrame(step);
      };
      starRaf = requestAnimationFrame(step);
      cleanups.push(() => {
        cancelAnimationFrame(starRaf);
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("mousemove", onMouseParallax);
        stars.style.removeProperty("--stars-y1");
        stars.style.removeProperty("--stars-y2");
        stars.style.removeProperty("--stars-x");
      });
    }

    /* ---------- 3. SCROLL-REVEAL ---------- */
    let observer, mutation;
    if (!reduced) {
      const REVEAL_SEL = [
        "article[data-testid]",
        "section[data-testid]",
        ".sat-brackets",
        "[data-reveal]",
      ].join(",");
      const DIRS = ["in-left", "in-right", "in-up", "in-down"];
      observer = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("sat-in");
            observer.unobserve(e.target);
          }
        });
      }, { rootMargin: "0px 0px -6% 0px", threshold: 0.08 });

      const scan = () => {
        document.querySelectorAll(REVEAL_SEL).forEach((el) => {
          if (el.classList.contains("sat-in") || el.classList.contains("sat-reveal-init")) return;
          if (el.closest(".sat-boot,.sat-cursor,[data-testid=r2a2-panel],[data-testid=pad-volume-bar]")) return;
          el.classList.add("sat-reveal", "sat-reveal-init");
          // Pick a random direction so cards fly in from mixed sides.
          el.setAttribute("data-anim", DIRS[Math.floor(Math.random() * DIRS.length)]);
          observer.observe(el);
        });
      };
      scan();
      mutation = new MutationObserver(() => scan());
      mutation.observe(document.body, { childList: true, subtree: true });
      cleanups.push(() => {
        observer.disconnect();
        mutation.disconnect();
      });
    }

    /* ---------- 4. NAV CURSOR-FOLLOW GLOW ---------- */
    const nav = document.querySelector("nav[data-testid=nav-desktop], nav");
    if (nav && !coarse && !reduced) {
      const onMove = (e) => {
        const r = nav.getBoundingClientRect();
        nav.style.setProperty("--nav-x", `${e.clientX - r.left}px`);
        nav.style.setProperty("--nav-y", `${e.clientY - r.top}px`);
      };
      const onLeave = () => nav.style.setProperty("--nav-x", `-9999px`);
      nav.addEventListener("mousemove", onMove);
      nav.addEventListener("mouseleave", onLeave);
      nav.classList.add("sat-nav-glow");
      cleanups.push(() => {
        nav.removeEventListener("mousemove", onMove);
        nav.removeEventListener("mouseleave", onLeave);
        nav.classList.remove("sat-nav-glow");
      });
    }

    /* ---------- 5. TAB CONTENT FADE ON SWITCH ---------- */
    if (!reduced) {
      const DIRS = ["left", "right", "up", "down"];
      const rand = () => DIRS[Math.floor(Math.random() * DIRS.length)];

      // Tab-content selectors: sub-tabs (POSTS/CHAT), main routes,
      // page bodies that live inside a keyed conditional render.
      const TAB_SEL = [
        // Any explicit opt-in
        "[data-tab-key]",
        // Convention: containers that render exclusively per tab
        "[data-testid=posts-list]",
        "[data-testid=community-messages]",
        "[data-testid=page-community-detail] > div:last-child",
      ].join(",");

      const tag = (el) => {
        if (!el || el.hasAttribute("data-tab-content")) return;
        el.setAttribute("data-tab-content", "1");
        el.setAttribute("data-dir", rand());
      };

      const scanTabs = () => {
        document.querySelectorAll(TAB_SEL).forEach(tag);
      };
      scanTabs();

      // Re-tag whenever a tab button is clicked so the panel replays.
      const onTabClick = (e) => {
        const btn = e.target.closest("[data-testid^=community-tab-],[data-testid^=nav-],button[role=tab]");
        if (!btn) return;
        // Wait a tick for React to render the new panel, then re-tag.
        setTimeout(() => {
          document.querySelectorAll(TAB_SEL).forEach((el) => {
            // Force a reflow-based animation replay by removing + re-adding.
            el.removeAttribute("data-tab-content");
            void el.offsetWidth;
            el.setAttribute("data-tab-content", "1");
            el.setAttribute("data-dir", rand());
          });
        }, 40);
      };
      document.addEventListener("click", onTabClick, true);

      // Also observe the DOM so newly-mounted tab bodies (route change) get tagged.
      const tabObs = new MutationObserver(scanTabs);
      tabObs.observe(document.body, { childList: true, subtree: true });

      cleanups.push(() => {
        document.removeEventListener("click", onTabClick, true);
        tabObs.disconnect();
      });
    }

    return () => cleanups.forEach((fn) => fn());
  }, []);

  return null;
}
